import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { getSafeCliRedirectUrl } from "@/common/cli-redirect";
import {
  getGatewayLoginUrl,
  hasGatewaySession,
} from "@/common/gateway-session";

const signinSearchSchema = z.object({
  redirect_url: z.string().optional(),
  product: z.string().optional(),
});

// /signin stays as a stable entry point (old links, bookmarks, marketing CTAs)
// but holds no auth UI of its own: an already-signed-in visitor bounces to the
// (sanitized) redirect target, everyone else hands off to /auth/start, which
// redirects into Tank's hosted sign-in.
export const Route = createFileRoute("/_rootLayout/signin")({
  validateSearch: zodValidator(signinSearchSchema),
  ssr: false,
  beforeLoad: async ({ search, preload }) => {
    // Preload (defaultPreload: "intent") must not run the redirect — it uses
    // reloadDocument, which would hard-navigate on hover. The real click
    // re-runs beforeLoad without preload. Mirrors admin.route.
    if (preload) return;
    // Sanitize the caller-supplied redirect_url through the allowlist so only
    // safe same-origin/product URLs are ever bounced to or forwarded
    // (defense-in-depth open-redirect guard; the backend validates too).
    const safeRedirectUrl = getSafeCliRedirectUrl(search.redirect_url);
    if (await hasGatewaySession()) {
      throw redirect(
        safeRedirectUrl.startsWith("/")
          ? { to: safeRedirectUrl }
          : { href: safeRedirectUrl },
      );
    }
    throw redirect({
      href: getGatewayLoginUrl(
        search.redirect_url ? safeRedirectUrl : undefined,
      ),
      // Relative href (/auth/start) would client-route into the server-only
      // Nitro route → SPA not-found; reloadDocument forces the real doc load.
      reloadDocument: true,
    });
  },
  component: SignInRoute,
});

// beforeLoad always redirects on a real navigation; this renders only for the
// preload early-return, so keep it inert.
function SignInRoute() {
  return null;
}
